// src/app/components/prompt-tracking/prompt-tracking-page.tsx

import { useState, useCallback } from 'react';
import { AIAssistantSidebar } from '../ai-assistant-sidebar';
import { PromptIntelligenceHeader } from './prompt-intelligence-header';
import { PromptAISection } from './prompt-ai-section';
import { PromptSearchFilter } from './prompt-search-filter';
import { PromptIntentGroup } from './prompt-intent-group';
import { AddPromptModal } from './add-prompt-modal';
import {
  INTENT_CONFIG,
  DEFAULT_EXPANDED_GROUPS,
} from '../../constants/prompt-tracking-constants';
import { usePromptTrackingData } from '../../hooks/prompt-tracking/usePromptTrackingData';
import type { PromptIntent } from '../../types/prompt-tracking-types';

interface PromptTrackingPageProps {
  isSidebarCollapsed: boolean;
  onSidebarToggle: (collapsed: boolean) => void;
}

export function PromptTrackingPage({
  isSidebarCollapsed,
  onSidebarToggle,
}: PromptTrackingPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntent, setSelectedIntent] = useState<PromptIntent | 'all'>('all');
  const [expandedGroups, setExpandedGroups] =
    useState<Record<string, boolean>>(DEFAULT_EXPANDED_GROUPS);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const brandName =
    localStorage.getItem('bm_brand') ||
    sessionStorage.getItem('bm_brand') ||
    'default';

  const { data, isLoading, error, refetch, updateFilters } =
    usePromptTrackingData({ searchQuery, selectedIntent, autoFetch: true });

  const prompts = data?.prompts ?? [];
  const stats = data?.summary ?? {
    totalPrompts: 0,
    avgRecommendationRate: 0,
    needsAttention: 0,
    upTrending: 0,
  };

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      updateFilters({ searchQuery: query });
    },
    [updateFilters]
  );

  const handleIntentChange = useCallback(
    (intent: PromptIntent | 'all') => {
      setSelectedIntent(intent);
      updateFilters({ intent });
    },
    [updateFilters]
  );

  const toggleGroup = (intent: string) =>
    setExpandedGroups((prev) => ({ ...prev, [intent]: !prev[intent] }));

  const getPromptsForIntent = (intent: PromptIntent) =>
    prompts.filter((p) => p.intent === intent);

  const contentMarginClass = isSidebarCollapsed ? 'lg:mr-12' : 'lg:mr-[380px]';

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      <div className={`flex-1 min-w-0 transition-all duration-300 ${contentMarginClass}`}>
        {/* Header owns its own padding — no outer wrapper to avoid double-padding */}
        <PromptIntelligenceHeader stats={stats} />

        <div className="px-4 md:px-8 py-6 md:py-8">
          <PromptAISection />

          <PromptSearchFilter
            searchQuery={searchQuery}
            selectedIntent={selectedIntent}
            onSearchChange={handleSearchChange}
            onIntentChange={handleIntentChange}
            onAddPrompt={() => setIsAddModalOpen(true)}
          />

          {isLoading && (
            <div
              className="flex items-center justify-center py-16"
              style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}
            >
              Loading prompt data…
            </div>
          )}

          {error && !isLoading && (
            <div
              className="p-6 rounded-lg text-center"
              style={{
                backgroundColor: 'var(--infrared-coral-10)',
                color: 'var(--infrared-coral)',
                fontSize: 'var(--text-sm)',
              }}
            >
              Failed to load prompts. Please try again.
            </div>
          )}

          {!isLoading && !error && (
            <div className="space-y-5 md:space-y-6">
              {INTENT_CONFIG.map((intentCfg) => {
                if (selectedIntent !== 'all' && selectedIntent !== intentCfg.key) return null;
                return (
                  <PromptIntentGroup
                    key={intentCfg.key}
                    intentConfig={intentCfg}
                    prompts={getPromptsForIntent(intentCfg.key)}
                    isExpanded={!!expandedGroups[intentCfg.key]}
                    onToggle={() => toggleGroup(intentCfg.key)}
                    onAnalysisComplete={refetch}
                    onDeleted={refetch}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="prompt-tracking"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
        />
      </div>

      <AddPromptModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCreated={refetch}
        brandName={brandName}
      />
    </div>
  );
}